'use client';
import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthApi } from '../../../hooks/useAuthApi';
import { useMemberApi } from '../../../hooks/useMemberApi';
import { ImpersonationDTO } from '../../../services/apiClient';
import { getUserId } from '../../../utils/tokenUtils';
import { MemberStatusConstants } from '../../../constants/MemberStatusConstants';
import Button from '../../../components/common/Button';

export default function ImpersonationRedirect() {
  const { memberId } = useParams<{ memberId: string }>();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { impersonateMemberLogin } = useAuthApi();
  const { getById } = useMemberApi();
  const backgroundImage = '/images/NS_Pattern%201_Navy_2025.webp';

  const handleImpersonation = async () => {
    if (!memberId) {
      setError('Member ID is required. Please provide a valid member ID.');
      setLoading(false);
      return;
    }

    // Validate GUID format
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!guidRegex.test(memberId)) {
      setError('Invalid member ID format. Please check the URL and try again.');
      setLoading(false);
      return;
    }

    const adminId = getUserId();
    if (!adminId) {
      setError('Admin authentication required. Please log in as an administrator.');
      setLoading(false);
      return;
    }

    const impersonateDto: ImpersonationDTO = {
      memberId: memberId,
      adminId: adminId,
    };

    try {
     
      
      // Fetch member to determine redirect path based on status
      const memberResponse = await getById(memberId);
      
      if (!memberResponse || !memberResponse.success || !memberResponse.data) {
        setError('Member not found. The member may have been deleted or the ID is incorrect.');
        setLoading(false);
        return;
      }

      const member = memberResponse.data;

      // Check if member is deleted
      if (member.isDeleted) {
        setError('This member account has been deleted and cannot be impersonated. Please contact an administrator if you believe this is an error.');
        setLoading(false);
        return;
      }

      // Check if member is archived
      if (member.isArchived) {
        setError('This member account has been archived and cannot be impersonated. Please unarchive the member first if you need to access their account.');
        setLoading(false);
        return;
      }

       // The hook will store tokens in sessionStorage (this new tab)
      await impersonateMemberLogin({ adminId, memberId: member.userID } as ImpersonationDTO, '');
      
      let redirectPath = '/admin/dashboard'; // Default fallback
      
      if (member.memberStatusId) {
        const statusId = member.memberStatusId.toLowerCase();
        
        // New registrations go to member setup
        if (statusId === MemberStatusConstants.NEW_REGISTRATION) {
          redirectPath = '/admin/membersetup';
        }
        // Active and other statuses go to dashboard
        else {
          redirectPath = '/admin/dashboard';
        }
      }
      
      // Wait 1 second so user can see the loading message
      setTimeout(() => {
        navigate(redirectPath);
      }, 1000);
      
    } catch (err: any) {
      console.error('Error impersonating member:', err);
      
      // Provide user-friendly error messages based on status code
      let errorMessage = 'An unexpected error occurred. ';
      
      if (err?.response?.status === 404) {
        errorMessage = 'Member not found. The member may have been deleted or does not exist.';
      } else if (err?.response?.status === 401 || err?.response?.status === 403) {
        errorMessage = 'You do not have permission to impersonate this member.';
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err?.message) {
        errorMessage = err.message;
      } else {
        errorMessage += 'Please contact support if this problem persists.';
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  };

  useEffect(() => {
    // If redirected back here with an error (e.g. member was deleted mid-session), show it immediately
    const errorParam = searchParams.get('error');
    if (errorParam) {
      setError(decodeURIComponent(errorParam));
      setLoading(false);
      return;
    }
    handleImpersonation();
  }, [memberId]);

  return (
    <div className="relative min-h-screen flex items-center justify-center px-4 py-8 sm:py-12">
      <div
        className="absolute inset-0 w-full min-h-full bg-cover bg-center transition-opacity duration-500 -z-10"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      />

      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg text-center z-10">
        <img
          src="/images/logo-linnear-navy.png"
          alt="Natshoot Logo"
          className="mx-auto mb-6 w-56"
        />

        <div className="mb-4">
          {loading ? (
            <>
              <div className="flex justify-center mb-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600"></div>
              </div>
              <h1 className="text-xl font-semibold">Logging in as member...</h1>
              <p className="text-gray-600 mt-2">Please wait while we switch your session.</p>
            </>
          ) : error ? (
            <>
              <div className="flex justify-center mb-6">
                <div className="rounded-full bg-amber-100 p-4">
                  <svg
                    className="w-12 h-12 text-amber-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
              </div>
              <h1 className="text-2xl font-semibold text-gray-800 mb-3">Unable to Login as Member</h1>
              <p className="text-gray-600 mb-6 leading-relaxed">{error}</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  type="button"
                  onClick={() => navigate('/admin/members')}
                  variant="primary"
                >
                  Go to Members
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    // Try to close the window if it was opened by window.open()
                    // Otherwise, navigate to members page
                    if (window.opener) {
                      window.close();
                    } else {
                      navigate('/admin/members');
                    }
                  }}
                  variant="secondary"
                >
                  Close
                </Button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
